import { useMutation } from "@tanstack/react-query";
import type { SafetyMessageOverride } from "@unbox/api";
import { handleError } from "@/api/error";
import { invalidateCampaignQueries } from "@/api/video-campaigns";
import type { LogActionType, TakeActionType } from "@/lib/assurance/action-types";
import { queryClient } from "@/providers/QueryClientProviderWrapper";
import { client, useScopedMerchantClient } from "../scoped-merchant-client";

type FlagReview = {
  flagId: string;
  actionTaken: "flag_is_correct" | "flag_dismissed";
  feedbackReason: string;
};

type ReviewAction = {
  actionType: TakeActionType | LogActionType;
  kind: "take" | "log";
  notes?: string;
  openCollaborationIds?: string[];
  targetCollaborationIds?: string[];
  campaignIds?: { advertiserId: string; campaignId: string }[];
};

type NewFlag = {
  riskTypeShort: string;
  riskTier: number;
  explanation?: string;
};

type ReviewSubmitPayload = {
  videoId: string;
  flagReviews: FlagReview[];
  newFlags?: NewFlag[];
  actions?: ReviewAction[];
  // When true, the backend starts a safety policy for the risks confirmed in this
  // review (one per video). `policyId` picks a specific policy; when omitted the
  // backend auto-resolves the highest-priority policy mapped to the confirmed risks.
  startPolicies?: boolean;
  policyId?: string;
  // Reviewer-authored copy for the policy's message steps, applied to this video only.
  // Requires `startPolicies` — the message is sent by the policy run.
  messageOverrides?: SafetyMessageOverride[];
};

export const useReviewVideo = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (payload: ReviewSubmitPayload) => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].assurance.review[":videoId"].$post({
            param: { ...scope.param, videoId: payload.videoId },
            json: {
              flagReviews: payload.flagReviews,
              newFlags: payload.newFlags,
              actions: payload.actions,
              startPolicies: payload.startPolicies,
              policyId: payload.policyId,
              messageOverrides: payload.messageOverrides,
            },
          })
        : await client.merchant[":merchantId"].assurance.review[":videoId"].$post({
            param: { ...scope.param, videoId: payload.videoId },
            json: {
              flagReviews: payload.flagReviews,
              newFlags: payload.newFlags,
              actions: payload.actions,
              startPolicies: payload.startPolicies,
              policyId: payload.policyId,
              messageOverrides: payload.messageOverrides,
            },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: (_, variables) => {
      if (!scope.merchantId) return;
      // Only invalidate the specific video detail — avoid refetching the list
      // so the review queue can keep its local state stable
      queryClient.invalidateQueries({
        queryKey: ["assurance-video", scope.merchantId, scope.workspaceId, variables.videoId],
      });
      queryClient.invalidateQueries({ queryKey: ["video", scope.merchantId, scope.workspaceId, variables.videoId] });
      // A review that removed the video from its ad campaign(s) must also refresh the campaign
      // pill/drawer/overview, otherwise the review-queue path leaves them stale (UNB-2024).
      if (variables.actions?.some((a) => a.actionType === "remove_from_ad_campaign")) {
        invalidateCampaignQueries(scope.merchantId, scope.workspaceId, variables.videoId);
      }
    },
  });
};
